import React, { useState, useRef, useEffect } from "react";
import {
  DatePicker,
  Form,
  Input,
  Select,
  Button,
  Popconfirm,
  message,
  Space,
} from "antd";
import dayjs from "dayjs";
import { ExclamationCircleOutlined, SearchOutlined } from "@ant-design/icons";
import PhoneInput from "react-phone-input-2";
import { EyeOutlined } from "@ant-design/icons";
import GenericTable from "../../../Components/GenericTable";
import GenericModal from "../../../Components/GenericModal";
import editIcon from "../../../Assets/Icons/edit.svg";
import editTableIcon from "../../../Assets/Icons/editTable.svg";
import delIcon from "../../../Assets/Icons/deleteTable.svg";
import addIcon from "../../../Assets/Icons/add-btn.svg";
import header from "../../../Assets/Icons/header.svg";
import { useNavigate } from "react-router-dom";
import { getCustomerDataById } from "../../../API/User/customer";
import { useParams } from "react-router-dom";
import { editCustomer } from "../../../API/User/customer";
import "./style.css";
import { deleteVehicle, editVehicle } from "../../../API/User/vehicle";
import { getCompanyData } from "../../../API/Admin/company";

const ViewCustomerDetails = () => {
  const [form] = Form.useForm();
  const [vehicleForm] = Form.useForm();
  const navigate = useNavigate();
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const [isDisabled, setIsDisabled] = useState(true);
  const { id } = useParams();
  const [customer, setCustomer] = useState(null);
  const [selectedVehicleKey, setSelectedVehicleKey] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const fileInputRef = useRef(null);
  const [logoFile, setLogoFile] = useState(null);
  const [recoveryCompanies, setRecoveryCompanies] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [isVehicleModalOpen, setIsVehicleModalOpen] = useState(false);
  const [vehicleSaving, setVehicleSaving] = useState(false);

  const handleEdit = () => {
    setIsDisabled((prev) => !prev);
  };

  const handleCancel = () => {
    form.resetFields();
    setSaved(false);
    message.info("Changes canceled");
  };

  const handleSubmit = async (values) => {
    setLoading(true);

    try {
      const res = await editCustomer({
        customer_id: id,
        customer_name: values?.fullName,
        email: values?.email,
        api_key: values?.api_key,
        domain: values?.domain,
        secret_pin: values?.pin,
        customer_phone: values?.phone,
        emergency_contact_person: values?.emergencyName,
        emergency_contact_phone: values?.emergencyPhone,
        vehicle_ids: vehicleData?.map((v) => v.key),
        profile_picture: logoFile,
        recovery_company_id: values?.recoveryCompany,
      });

      if (Math.floor(res.status / 100) === 2) {
        messageApi.success("Customer updated successfully!");
        setIsDisabled(true);
        fetchCustomerById(id);
      }
    } catch (err) {
      messageApi.error(
        err.response?.data?.email
          ? err.response.data.email[0]
          : "Error updating customer",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    if (!customer?.id) {
      messageApi.error("Customer ID is missing.");
      return;
    }
    navigate("/add-vehicles", { state: { customerId: customer.id } });
  };

  const fetchCustomerById = async (id) => {
    try {
      setLoading(true);
      const response = await getCustomerDataById(id);
      if (Math.floor(response?.status / 100) === 2) {
        const customerData = response?.data?.customer;
        if (customerData) {
          setCustomer(customerData);

          form.setFieldsValue({
            fullName: customerData?.customer_name,
            email: customerData?.email,
            phone: customerData?.customer_phone,
            emergencyName: customerData?.emergency_contact_person,
            emergencyPhone: customerData?.emergency_contact_phone,
            api_key: customerData?.api_key || "",
            pin: customerData?.secret_pin || "",
            domain: customerData?.domain || "",
            recoveryCompany: customerData?.recovery_company?.id || null,
          });

          const fullLogoUrl = customerData.customer_profile_picture
            ? `${process.env.REACT_APP_MEDIA_URL}${customerData?.customer_profile_picture}`
            : null;

          setLogoUrl(fullLogoUrl);
          const formattedVehicles = customerData.vehicles?.map((v) => ({
            key: v.id,
            vehicle: v.vehicle_name,
            licensePlate: v.license_plate_number,
            year: v.manufacturing_year,
            trackerId: v.tracker_id,
            driverName: v.driver_name,
            driverContact: v.driver_contact,
          }));

          setVehicleData(formattedVehicles || []);
        }
      } else {
        message.error("Unexpected response from server.");
      }
    } catch (err) {
      message.error("Failed to fetch customer.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchRecoveryCompanies = async () => {
      try {
        const res = await getCompanyData();

        if (Math.floor(res.status / 100) === 2) {
          const companies = res.data.companies
            .filter((c) => c.status === "Active" || c.status === null)
            .map((c) => ({
              label: c.company_name,
              value: c.id,
            }));

          setRecoveryCompanies(companies);
        }
      } catch (error) {
        message.error("Failed to load recovery companies");
      }
    };

    fetchRecoveryCompanies();
  }, []);

  useEffect(() => {
    if (id) {
      fetchCustomerById(id);

    }
  }, [id]);

  const [vehicleData, setVehicleData] = useState([]);

  const handleDelete = async (key, vehicleId) => {
    try {
      if (!vehicleId) {
        setVehicleData((prev) => prev.filter((item) => item.key !== key));
        return;
      }
      const response = await deleteVehicle(vehicleId);
      if (Math.floor(response.status / 100) === 2) {
        setVehicleData((prev) => prev.filter((item) => item.key !== key));

        const successMsg =
          response?.data?.message || "Vehicle deleted successfully";
        messageApi.success(successMsg);
      } else {
        const errorMsg = response?.data?.message || "Failed to unlink vehicle";
        messageApi.error(errorMsg);
      }
    } catch (err) {
      const errorMsg =
        err.response?.data?.message || "Failed to unlink vehicle";
      messageApi.error(errorMsg);
      console.error(err);
    }
  };

  const handleEditVehicle = (record) => {
    setEditingVehicle(record);
    vehicleForm.setFieldsValue({
      licensePlate: record.licensePlate,
      year: record.year,
    });
    setIsVehicleModalOpen(true);
  };

  const handleVehicleModalCancel = () => {
    setIsVehicleModalOpen(false);
    setEditingVehicle(null);
    vehicleForm.resetFields();
  };

  const handleVehicleModalOk = async () => {
    try {
      const values = await vehicleForm.validateFields();
      setVehicleSaving(true);

      const res = await editVehicle({
        vehicle_id: editingVehicle.key,
        customer: id,
        license_plate_number: values.licensePlate,
        manufacturing_year: parseInt(values.year, 10),
      });

      if (Math.floor(res.status / 100) === 2) {
        setVehicleData((prev) =>
          prev.map((item) =>
            item.key === editingVehicle.key
              ? { ...item, licensePlate: values.licensePlate, year: values.year }
              : item,
          ),
        );
        messageApi.success("Vehicle updated successfully!");
        setIsVehicleModalOpen(false);
        setEditingVehicle(null);
        vehicleForm.resetFields();
      } else {
        messageApi.error("Failed to update vehicle.");
      }
    } catch (err) {
      if (err?.errorFields) return;
      const backendMsg = err.response?.data
        ? Object.values(err.response.data).flat().join(" | ")
        : "Failed to update vehicle.";
      messageApi.error(backendMsg);
    } finally {
      setVehicleSaving(false);
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
          .toString()
          .toLowerCase()
          .includes(value.toLowerCase())
        : "",
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    setSearchText("");
    confirm();
  };
  const highlightText = (text) => {
    if (!searchText) return text;
    const regex = new RegExp(`(${searchText})`, "gi");
    const parts = String(text).split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <span
          key={index}
          style={{
            backgroundColor: "yellow",
            color: "#d32f2f",
            fontWeight: "bold",
          }}
        >
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  const vehicleColumns = [
    {
      title: "Vehicle",
      dataIndex: "vehicle",
      key: "vehicle",
      render: (text) => highlightText(text),
      ...getColumnSearchProps("vehicle"),
    },
    {
      title: "License Plate Number",
      dataIndex: "licensePlate",
      key: "licensePlate",
      render: (text) => highlightText(text),
      ...getColumnSearchProps("licensePlate"),
    },
    {
      title: "Year",
      dataIndex: "year",
      key: "year",
      sorter: (a, b) => new Date(a.year) - new Date(b.year),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div style={{ display: "flex", gap: "13px", alignItems: "center" }}>
          <a
            onClick={(e) => {
              e.stopPropagation();
              handleEditVehicle(record);
            }}
          >
            <img src={editTableIcon} alt="Edit" />
          </a>
          <Popconfirm
            title="Are you sure to delete this?"
            icon={<ExclamationCircleOutlined style={{ color: "red" }} />}
            okText="Yes"
            cancelText="Cancel"
            onConfirm={() => handleDelete(record.key, record.key)}
          >
            <a onClick={(e) => e.stopPropagation()}>
              <img src={delIcon} alt="Delete" />
            </a>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="view-customer">
      {contextHolder}
      <div className="customer-form">
        <div className="header">
          <img src={header} alt="header Icon" />
        </div>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <div className="customer-container">
            <div className="profile-details">
              <div className="logo-wrapper">
                {logoUrl ? (
                  <img src={logoUrl} alt="Profile" className="logo-icon" />
                ) : (
                  "Null"
                )}

                {!isDisabled && (
                  <div
                    className="edit-overlay"
                    onClick={() => fileInputRef.current.click()}
                  >
                    <img src={editIcon} alt="edit logo" className="edit-icon" />
                  </div>
                )}

                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) return;

                    const allowedExtensions = ["png", "jpg", "jpeg", "jfif"];
                    const fileExtension = file.name
                      .split(".")
                      .pop()
                      .toLowerCase();

                    if (allowedExtensions.includes(fileExtension)) {
                      setLogoUrl(URL.createObjectURL(file));
                      setLogoFile(file);
                    } else {
                      message.error(
                        "Only image files (.png, .jpg, .jpeg, .jfif) are allowed",
                      );
                    }
                  }}
                />
              </div>

              <div className="profile-wrapper">
                <div className="info">
                  <div className="name">{customer?.customer_name}</div>
                  <div className="email">{customer?.email}</div>
                </div>
                <Button
                  type="default"
                  onClick={handleEdit}
                  className="edit-btn"
                >
                  <img
                    src={isDisabled ? editIcon : undefined}
                    alt={isDisabled ? "edit user" : "view user"}
                    className="btn-icon"
                    style={{ display: isDisabled ? "inline" : "none" }}
                  />
                  {!isDisabled && <EyeOutlined />}
                  {isDisabled ? "Edit" : "View"}
                </Button>
              </div>
            </div>

            <div className="heading">Customer Information</div>
            <div className="details">
              <div className="input-group">
                <div className="title">Full Name</div>
                <Form.Item name="fullName">
                  <Input disabled={isDisabled} />
                </Form.Item>
              </div>

              <div className="input-group">
                <div className="title">Email Address</div>
                <Form.Item name="email">
                  <Input disabled={isDisabled} />
                </Form.Item>
              </div>

              <div className="input-group">
                <div className="title">Phone Number</div>
                <Form.Item
                  name="phone"
                  rules={[
                    { required: true, message: "Please enter phone number!" },
                  ]}
                >
                  <PhoneInput
                    country={"jm"}
                    placeholder="Enter phone number"
                    inputClass={`phone-input ${isDisabled ? "disabled" : ""}`}
                    specialLabel={false}
                    disabled={isDisabled}
                  />
                </Form.Item>
              </div>

              <div className="input-group">
                <div className="title">API Key</div>
                <Form.Item
                  name="api_key"
                  rules={[{ required: true, message: "Please enter API key" }]}
                >
                  <Input placeholder="Enter API Key" disabled={isDisabled} />
                </Form.Item>
              </div>

              <div className="input-group">
                <div className="title">Domain</div>
                <Form.Item
                  name="domain"
                  rules={[
                    { required: true, message: "Please enter Domain" },
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();

                        if (!value.startsWith("https://")) {
                          return Promise.reject(
                            new Error("Domain must start with https://")
                          );
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input placeholder="Enter Domain" disabled={isDisabled} />
                </Form.Item>
              </div>

              <div className="input-group">
                <div className="title">Secret Pin</div>
                <Form.Item
                  name="pin"
                  rules={[
                    {
                      required: true,
                      message: "Please enter your Secret Pin",
                    },
                    {
                      pattern: /^\d{4}$/,
                      message: "Pin must be exactly 4 numeric digits",
                    },
                  ]}
                >
                  <Input.Password
                    placeholder="Enter Secret Pin"
                    maxLength={4}
                    disabled={isDisabled}
                  />
                </Form.Item>
              </div>

              <div className="input-group">
                <div className="title">Emergency Contact Name</div>
                <Form.Item name="emergencyName">
                  <Input disabled={isDisabled} />
                </Form.Item>
              </div>

              <div className="input-group">
                <div className="title">Emergency Contact Number</div>
                <Form.Item name="emergencyPhone">
                  <PhoneInput
                    country={"jm"}
                    placeholder="Enter Contact Number"
                    inputClass="phone-input"
                    specialLabel={false}
                    disabled={isDisabled}
                  />
                </Form.Item>
              </div>


              <div className="input-group">
                <div className="title">Recovery Company</div>
                <Form.Item name="recoveryCompany">
                  <Select options={recoveryCompanies} disabled={isDisabled} />
                </Form.Item>
              </div>
            </div>

          </div>
          <div className="form-actions">
            <Button disabled={isDisabled} onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              className="save-btn"
              type="primary"
              htmlType="submit"
              disabled={isDisabled}
            >
              Save Changes
            </Button>
          </div>
        </Form>

        <div className="table-container">
          <div className="button-wrapper">
            <div className="heading">Vehicle List</div>
            <div className="add-btn">
              <Button type="default" onClick={handleAdd} className="add-btn">
                <img src={addIcon} alt="add" />
                Add Vehicle
              </Button>
            </div>
          </div>
          <div className="table">
            <GenericTable
              dataSource={vehicleData}
              columns={vehicleColumns}
              onRow={(record) => ({
                onClick: () => {
                  setSelectedVehicleKey(record.key);
                  form.setFieldsValue({
                    licensePlate: record.licensePlate,
                    vehicle: record.vehicle,
                  });
                },
              })}
            />
          </div>
        </div>

        <GenericModal
          title="Edit Vehicle"
          open={isVehicleModalOpen}
          onOk={handleVehicleModalOk}
          onCancel={handleVehicleModalCancel}
          okText="Save"
          okButtonProps={{ loading: vehicleSaving }}
        >
          <Form form={vehicleForm} layout="vertical" requiredMark={false}>
            <Form.Item
              name="licensePlate"
              label="License Plate Number"
              rules={[
                { required: true, message: "Please enter a license plate number" },
              ]}
            >
              <Input placeholder="Enter License Plate Number" />
            </Form.Item>

            <Form.Item
              name="year"
              label="Manufacturing Year"
              rules={[
                { required: true, message: "Please enter vehicle year" },
                {
                  pattern: /^\d{4}$/,
                  message: "Please enter a valid 4-digit year",
                },
              ]}
            >
              <Input placeholder="Enter Manufacturing Year" maxLength={4} />
            </Form.Item>
          </Form>
        </GenericModal>
      </div>
    </div>
  );
};

export default ViewCustomerDetails;
